import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  FileText, Plus, Edit, Trash2, Loader2, Save, Eye,
  Menu, Footprints, Scale, GripVertical, ExternalLink, RefreshCw
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PagesManager({ adminKey }) {
  const { t } = useLanguage();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageDialog, setPageDialog] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [activeLanguage, setActiveLanguage] = useState('fr');

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/pages`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      const data = await response.json();
      setPages(data.pages || []);
    } catch (error) {
      toast.error(t('admin.load_error'));
    } finally {
      setLoading(false);
    }
  }, [adminKey, t]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const seedLegalPages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/pages/seed-legal`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey }
      });
      const data = await response.json();
      toast.success(data.message);
      fetchPages();
    } catch (error) {
      toast.error(t('admin.load_error'));
    }
  };

  const handleSave = async () => {
    if (!editingPage?.slug || !editingPage?.title?.fr) {
      toast.error(t('admin.slug_title_required'));
      return;
    }

    setSaving(true);
    try {
      const url = editingPage.page_id 
        ? `${API_URL}/api/admin/pages/${editingPage.page_id}`
        : `${API_URL}/api/admin/pages`;
      
      const response = await fetch(url, {
        method: editingPage.page_id ? 'PUT' : 'POST',
        headers: { 
          'X-Admin-Key': adminKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingPage)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error saving page');
      }

      toast.success(editingPage.page_id ? t('admin.page_updated') : t('admin.page_created'));
      setPageDialog(false);
      setEditingPage(null);
      fetchPages();
    } catch (error) {
      toast.error(error.message || t('admin.config_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm(t('admin.delete') + '?')) return;

    try {
      await fetch(`${API_URL}/api/admin/pages/${pageId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey }
      });
      toast.success(t('admin.page_deleted'));
      setPages(prev => prev.filter(p => p.page_id !== pageId));
    } catch (error) {
      toast.error(t('admin.delete_error'));
    }
  };

  const openNewPage = () => {
    setEditingPage({
      title: { fr: '', en: '' },
      slug: '',
      content: { fr: '', en: '' },
      meta_description: { fr: '', en: '' },
      show_in_header: false,
      show_in_footer: false,
      is_legal: false,
      order: pages.length,
      is_published: true
    });
    setPageDialog(true);
  };

  const openEditPage = (page) => {
    setEditingPage({ ...page });
    setPageDialog(true);
  };

  const updatePageField = (field, value) => {
    setEditingPage(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (field, lang, value) => {
    setEditingPage(prev => ({
      ...prev,
      [field]: { ...(prev[field] || {}), [lang]: value }
    }));
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const legalPages = pages.filter(p => p.is_legal);
  const customPages = pages.filter(p => !p.is_legal);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">{t('admin.pages_manager')}</h2>
          <p className="text-muted-foreground">{pages.length} {t('admin.pages_count')} • {t('admin.create_manage_pages')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPages}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('admin.refresh')}
          </Button>
          <Button variant="outline" onClick={seedLegalPages}>
            <Scale className="h-4 w-4 mr-2" />
            {t('admin.create_legal_pages')}
          </Button>
          <Button onClick={openNewPage}>
            <Plus className="h-4 w-4 mr-2" />
            {t('admin.new_page')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Legal Pages */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                {t('admin.legal_pages')} ({legalPages.length})
              </CardTitle>
              <CardDescription>{t('admin.policies_terms')}</CardDescription>
            </CardHeader>
            <CardContent>
              {legalPages.length === 0 ? (
                <div className="text-center py-8">
                  <Scale className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground mb-4">{t('admin.no_legal_pages')}</p>
                  <Button variant="outline" onClick={seedLegalPages}>
                    {t('admin.create_legal_pages')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {legalPages.map(page => (
                    <div 
                      key={page.page_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Scale className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm">{page.title?.fr || page.slug}</p>
                          <p className="text-xs text-muted-foreground">/{page.slug}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {page.show_in_footer && (
                          <Footprints className="h-4 w-4 text-muted-foreground" title={t('admin.show_in_footer')} />
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEditPage(page)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <a href={`/page/${page.slug}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Custom Pages */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('admin.custom_pages')} ({customPages.length})
              </CardTitle>
              <CardDescription>{t('admin.your_site_pages')}</CardDescription>
            </CardHeader>
            <CardContent>
              {customPages.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground mb-4">{t('admin.no_custom_pages')}</p>
                  <Button onClick={openNewPage}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t('admin.create_page')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {customPages.map(page => (
                    <div 
                      key={page.page_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm">{page.title?.fr || page.slug}</p>
                          <p className="text-xs text-muted-foreground">/{page.slug}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {page.show_in_header && (
                          <Menu className="h-4 w-4 text-muted-foreground" title={t('admin.show_in_menu')} />
                        )}
                        {page.show_in_footer && (
                          <Footprints className="h-4 w-4 text-muted-foreground" title={t('admin.show_in_footer')} />
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEditPage(page)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(page.page_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <a href={`/page/${page.slug}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Page Editor Dialog */}
      <Dialog open={pageDialog} onOpenChange={setPageDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPage?.page_id ? t('admin.edit_page') : t('admin.new_page')}</DialogTitle>
          </DialogHeader>

          {editingPage && (
            <div className="space-y-6">
              {/* Language Tabs */}
              <div className="flex gap-2 border-b pb-2">
                <Button 
                  variant={activeLanguage === 'fr' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setActiveLanguage('fr')}
                >
                  🇫🇷 {t('admin.french')}
                </Button>
                <Button 
                  variant={activeLanguage === 'en' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setActiveLanguage('en')}
                >
                  🇬🇧 {t('admin.english')}
                </Button>
              </div>

              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('admin.title_required')} ({activeLanguage === 'fr' ? t('admin.french') : t('admin.english')}) *</Label>
                  <Input 
                    value={editingPage.title?.[activeLanguage] || ''} 
                    onChange={(e) => {
                      updateNestedField('title', activeLanguage, e.target.value);
                      if (!editingPage.page_id && activeLanguage === 'fr') {
                        updatePageField('slug', generateSlug(e.target.value));
                      }
                    }}
                    placeholder={t('admin.title_required')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.slug_url')} *</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">/page/</span>
                    <Input 
                      value={editingPage.slug || ''} 
                      onChange={(e) => updatePageField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                      placeholder="url-de-la-page"
                    />
                  </div>
                </div>
              </div>

              {/* Meta Description */}
              <div className="space-y-2">
                <Label>{t('admin.seo_description')} ({activeLanguage === 'fr' ? t('admin.french') : t('admin.english')})</Label>
                <Input 
                  value={editingPage.meta_description?.[activeLanguage] || ''} 
                  onChange={(e) => updateNestedField('meta_description', activeLanguage, e.target.value)}
                  placeholder={t('admin.seo_placeholder')}
                />
              </div>

              {/* Content */}
              <div className="space-y-2">
                <Label>{t('admin.content')} ({activeLanguage === 'fr' ? t('admin.french') : t('admin.english')}) *</Label>
                <Textarea 
                  value={editingPage.content?.[activeLanguage] || ''} 
                  onChange={(e) => updateNestedField('content', activeLanguage, e.target.value)}
                  placeholder={t('admin.content_placeholder')}
                  className="min-h-[300px] font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  {t('admin.markdown_support')}
                </p>
              </div>

              {/* Options */}
              <Card>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>{t('admin.show_in_header')}</Label>
                        <p className="text-xs text-muted-foreground">{t('admin.adds_nav_link')}</p>
                      </div>
                      <Switch 
                        checked={editingPage.show_in_header} 
                        onCheckedChange={(v) => updatePageField('show_in_header', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>{t('admin.show_footer')}</Label>
                        <p className="text-xs text-muted-foreground">{t('admin.adds_footer_link')}</p>
                      </div>
                      <Switch 
                        checked={editingPage.show_in_footer} 
                        onCheckedChange={(v) => updatePageField('show_in_footer', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>{t('admin.legal_page')}</Label>
                        <p className="text-xs text-muted-foreground">{t('admin.categorizes_legal')}</p>
                      </div>
                      <Switch 
                        checked={editingPage.is_legal} 
                        onCheckedChange={(v) => updatePageField('is_legal', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>{t('admin.published')}</Label>
                        <p className="text-xs text-muted-foreground">{t('admin.makes_visible')}</p>
                      </div>
                      <Switch 
                        checked={editingPage.is_published} 
                        onCheckedChange={(v) => updatePageField('is_published', v)}
                      />
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <Label>{t('admin.display_order')}</Label>
                    <Input 
                      type="number"
                      value={editingPage.order || 0} 
                      onChange={(e) => updatePageField('order', parseInt(e.target.value) || 0)}
                      className="w-24"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPageDialog(false)}>{t('admin.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {t('admin.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
